import {
    Banknote,
    Bell,
    CheckCircle2,
    Clock,
    FileText,
    History,
    LayoutDashboard,
    LogOut,
    Menu as MenuIcon,
    Search,
    Settings,
    Tag,
    XCircle,
} from 'lucide-react'
import {
    AppBar,
    Avatar,
    Breadcrumbs,
    Button,
    CircularProgress,
    Divider,
    IconButton,
    Link,
    ListItemIcon,
    Menu,
    MenuItem,
    Stack,
    Tooltip,
    Typography,
    useMediaQuery,
    useTheme,
} from '@mui/material'
import { deepOrange } from '@mui/material/colors'
import { Box } from '@mui/system'
import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useLocation, useNavigate } from 'react-router-dom'
import { useAccount } from '../../context/account_context'
import { useCashier } from '../../context/cashier_context'
import { useConfigs } from '../../context/configs_context'
import { doLogout } from '../../services/auth'
import { SidebarMode } from '../Layout'
import { Navigation, navMenu } from '../LeftPanel/nav'
import { LoginModal } from '../LoginModal'
import { listDocuments } from '../../services/document'
import {
    DocumentInfo,
    DocumentState,
    DocumentStateLabel,
} from '../../pages/Document/Home'
import { downloadFile } from '../../services/media'
import { getDateString } from '../../utils/maks'
import { useLoading } from '../../context/loading_context'
import { verifyCompanyPayment } from '../../services/company'
import DarkModeIcon from '@mui/icons-material/DarkMode'
import LightModeIcon from '@mui/icons-material/LightMode'
import { openCommandPalette } from '../CommandPalette'
import { useThemeMode } from '../../context/theme_mode_context'

interface Props {
    drawerWidth: number
    headerHeight: number
    setShowMobile(value: boolean): void
    hideSideBar: number
    sidebarMode: number
    setSidebarMode(value: number): void
}

interface Notification {
    icon: JSX.Element
    text: string
    link?: string
}

const Header = ({
    drawerWidth,
    setShowMobile,
    headerHeight,
    hideSideBar,
    sidebarMode,
    setSidebarMode,
}: Props) => {
    const { t } = useTranslation()
    const { mode, toggleMode } = useThemeMode()
    const loading = useLoading()
    const theme: any = useTheme()
    const isSm = useMediaQuery(theme.breakpoints.down('md'))
    const isMd = useMediaQuery(theme.breakpoints.down('lg'))
    const appBarRef = useRef<HTMLDivElement>(null)
    const navigate = useNavigate()
    const { pathname } = useLocation()
    const { accountInfo } = useAccount()
    const [nav, setNav] = useState<Navigation | null>(null)
    const [subnav, setSubNav] = useState<Navigation | null>(null)
    const { configs } = useConfigs()
    const { loggedCashier, setOpenCashierHashModal } = useCashier()
    const [anchor, setAnchor] = useState<HTMLElement | null>(null)
    const [anchorType, setAnchorType] = useState<
        'user' | 'notification' | 'document'
    >()
    const [notifications, setNotifications] = useState<Notification[]>([])
    const [notificationsNotSeen, setNotificationsNotSeen] = useState(0)
    const [openLoginModal, setOpenLoginModal] = useState(false)
    const [documents, setDocuments] = useState<DocumentInfo[]>([])
    const documentRef = useRef<HTMLButtonElement>(null)
    const documentIntervalRef = useRef<NodeJS.Timeout>()

    const canChangeCashier =
        accountInfo?.isMaster ||
        !!accountInfo?.permissions?.includes('Caixas:Modificar caixa')

    const logout = () => {
        doLogout()
        window.location.href = `${window.origin}/`
    }

    useEffect(() => {
        getNotifications()
    }, [])

    useEffect(() => {
        navMenu.forEach((nav) => {
            const subnav = nav.subitems?.find(
                (subnav) => subnav.link === pathname.replace(/\/\d+$/, '')
            )
            if (subnav) {
                setNav(nav)
                setSubNav(subnav)
            }
        })
    }, [pathname])

    useEffect(() => {
        if (!!anchor && anchorType === 'document') {
            getDocuments()

            const hasActivePending = () =>
                documents.some(
                    (d) =>
                        d.documentState === DocumentState.Pending &&
                        (getDocumentMinutesSince(d) ?? 0) <= 5
                )

            if (hasActivePending()) {
                documentIntervalRef.current = setInterval(() => {
                    getDocuments()
                }, 5000)
            }

            return () => {
                clearInterval(documentIntervalRef.current)
            }
        }
    }, [anchor, anchorType, documents])

    const getNotifications = async () => {
        const newNotifications: Notification[] = []

        const [companyPayment] = await Promise.all([verifyCompanyPayment()])

        if (companyPayment.notifyPayment) {
            newNotifications.push(...notifications, {
                icon: <Banknote fontSize="medium" />,
                text: '¡Atención! Recomendamos encarecidamente a quienes aún no hayan pagado su cuota mensual que lo hagan. Recuerden que la fecha límite de pago es del 1 al 5 de cada mes.',
            })
        }

        setNotifications(newNotifications)
        setNotificationsNotSeen(newNotifications.length)
    }

    const getDocumentMinutesSince = (document: DocumentInfo): number | null => {
        if (!document.date || !document.hour) return null
        const date = new Date(`${document.date.split('T')[0]} ${document.hour}`)
        const summerTime = document.isSummerTime ? 1 : 0
        date.setHours(date.getHours() + configs.timeZone + summerTime)

        const now = new Date()
        const nowOffset = -(now.getTimezoneOffset() / 60)
        now.setHours(
            now.getHours() -
                nowOffset +
                configs.timeZone +
                Number(configs.isSummerTime)
        )

        return Math.max(0, Math.floor((now.valueOf() - date.valueOf()) / 60000))
    }

    const getDocuments = async () => {
        if (!accountInfo?.id) return

        try {
            const res = await listDocuments({
                skip: 1,
                take: 10,
                id_usuario: [accountInfo.id],
                id_filial: [],
                id_tipo_documento: [],
            })
            setDocuments(res.data)
        } catch (error) {
            console.log(error)
        }
    }

    const canSeeDashboard =
        accountInfo?.isMaster ||
        !!accountInfo?.permissions?.includes('Painel:Visualizar')

    return (
        <>
            <LoginModal
                open={openLoginModal}
                handleClose={() => setOpenLoginModal(false)}
            />
            <Menu
                open={Boolean(anchor)}
                onClose={() => {
                    setAnchor(null)
                }}
                anchorEl={anchor}
                sx={{
                    top: '10px',
                }}
            >
                {anchorType === 'user' && (
                    <>
                        <MenuItem>
                            {t('Olá')}, {accountInfo?.name}
                        </MenuItem>
                        <Divider />
                        <MenuItem onClick={logout}>
                            <ListItemIcon>
                                <LogOut fontSize="small" />
                            </ListItemIcon>
                            {t('Salir')}
                        </MenuItem>
                    </>
                )}
                {anchorType === 'notification' && (
                    <Stack maxWidth={'450px'}>
                        <Typography
                            textAlign={'center'}
                            p={'5px'}
                            fontWeight={'bold'}
                            fontSize={'18px'}
                        >
                            Notificações
                        </Typography>
                        <Divider />
                        {notifications.map((notification) => (
                            <MenuItem
                                onClick={() => {
                                    if (notification.link)
                                        navigate(notification.link)
                                    setAnchor(null)
                                }}
                                sx={{
                                    fontSize: '13px',
                                    p: '10px',
                                    width: '100%',
                                    whiteSpace: 'normal',
                                }}
                            >
                                <ListItemIcon>{notification.icon}</ListItemIcon>
                                {notification.text}
                            </MenuItem>
                        ))}
                        {!notifications.length && (
                            <MenuItem
                                sx={{
                                    fontSize: '13px',
                                    p: '10px',
                                    width: '100%',
                                    whiteSpace: 'normal',
                                    display: 'flex',
                                    justifyContent: 'center',
                                }}
                            >
                                Você não possui nenhuma notificação.
                            </MenuItem>
                        )}
                    </Stack>
                )}
                {anchorType === 'document' && (
                    <Stack width={{ xs: '300px', md: '450px' }}>
                        <Typography
                            textAlign={'center'}
                            p={'5px'}
                            fontWeight={'bold'}
                            fontSize={'18px'}
                        >
                            Documentos
                        </Typography>
                        <Divider />
                        {documents.map((document, index) => {
                            const date = getDateString({
                                date: document.date ?? '',
                                hour: document.hour ?? '',
                                configs: configs,
                                isSummerTime: document.isSummerTime ?? false,
                            })

                            const isPending =
                                document.documentState === DocumentState.Pending
                            const minutes = getDocumentMinutesSince(document)
                            const isExpiredPending =
                                isPending && minutes !== null && minutes > 5

                            const displayState = isExpiredPending
                                ? t('Expirado')
                                : t(DocumentStateLabel[document.documentState])

                            const accent: Record<number, string> = {
                                0: '#3a49e7',
                                1: '#16a34a',
                                2: '#dc2626',
                                3: '#dc2626',
                            }
                            const softBg: Record<number, string> = {
                                0: 'rgba(79, 70, 229, 0.10)',
                                1: 'rgba(34, 197, 94, 0.12)',
                                2: 'rgba(239, 68, 68, 0.12)',
                                3: 'rgba(239, 68, 68, 0.12)',
                            }
                            const stateColor = isExpiredPending
                                ? '#dc2626'
                                : accent[document.documentState]

                            const stateIcon =
                                document.documentState ===
                                DocumentState.Completed ? (
                                    <CheckCircle2 size={18} color="#16a34a" />
                                ) : document.documentState ===
                                      DocumentState.Error ||
                                  document.documentState ===
                                      DocumentState.Canceled ? (
                                    <XCircle size={18} color="#dc2626" />
                                ) : (
                                    <Clock size={18} color="#3a49e7" />
                                )

                            return (
                                <MenuItem
                                    key={document.id ?? index}
                                    onClick={() => {
                                        if (isExpiredPending) return
                                        if (
                                            document.documentState ===
                                            DocumentState.Pending
                                        )
                                            return
                                        downloadFile(document.documentPath)
                                    }}
                                    sx={{
                                        fontSize: '13px',
                                        p: '10px',
                                        width: '100%',
                                        whiteSpace: 'normal',
                                        borderBottom:
                                            '1px solid var(--border-soft)',
                                        display: 'flex',
                                        alignItems: 'center',
                                        '&:hover': {
                                            backgroundColor:
                                                'var(--surface-muted)',
                                        },
                                    }}
                                >
                                    <ListItemIcon
                                        sx={{
                                            minWidth: '32px',
                                            color: stateColor,
                                        }}
                                    >
                                        {stateIcon}
                                    </ListItemIcon>
                                    <Stack
                                        direction={'column'}
                                        mr={'20px'}
                                        width={'100%'}
                                    >
                                        <Typography
                                            fontSize={'0.875rem'}
                                            fontWeight={600}
                                        >
                                            {document.documentType.name}
                                        </Typography>
                                        <Typography
                                            fontSize={'0.75rem'}
                                            color={'#71797E'}
                                        >
                                            {date}
                                        </Typography>
                                        {isPending && !isExpiredPending && (
                                            <Stack
                                                direction={'row'}
                                                alignItems={'center'}
                                                spacing={1}
                                                mt="4px"
                                            >
                                                <CircularProgress
                                                    size={12}
                                                    thickness={5}
                                                    sx={{
                                                        color: '#3a49e7',
                                                    }}
                                                />
                                                <Typography
                                                    fontSize={'0.72rem'}
                                                    color={'#3a49e7'}
                                                >
                                                    {t('Gerando documento…')}
                                                </Typography>
                                            </Stack>
                                        )}
                                        {isExpiredPending && (
                                            <Typography
                                                fontSize={'0.72rem'}
                                                color={'#dc2626'}
                                                mt="2px"
                                            >
                                                {t(
                                                    'Pendente há mais de 5 minutos'
                                                )}
                                            </Typography>
                                        )}
                                    </Stack>
                                    <Box
                                        sx={{
                                            color: stateColor,
                                            backgroundColor: isExpiredPending
                                                ? softBg[3]
                                                : softBg[
                                                      document.documentState
                                                  ],
                                            py: '5px',
                                            px: '10px',
                                            borderRadius: '8px',
                                            ml: 'auto',
                                            width: '86px',
                                            textAlign: 'center',
                                            fontWeight: 600,
                                            fontSize: '0.75rem',
                                            whiteSpace: 'nowrap',
                                            opacity: isExpiredPending ? 0.7 : 1,
                                        }}
                                    >
                                        {displayState}
                                    </Box>
                                </MenuItem>
                            )
                        })}
                        {!documents.length && (
                            <MenuItem
                                sx={{
                                    fontSize: '13px',
                                    p: '10px',
                                    width: '100%',
                                    whiteSpace: 'normal',
                                    display: 'flex',
                                    justifyContent: 'center',
                                }}
                            >
                                Você não possui nenhum documento.
                            </MenuItem>
                        )}
                    </Stack>
                )}
            </Menu>
            <AppBar
                position="sticky"
                sx={{
                    width: {
                        xs: '100%',
                        lg:
                            hideSideBar || sidebarMode === SidebarMode.Small
                                ? '100%'
                                : `calc(100% - ${drawerWidth}px)`,
                    },
                    ml: {
                        xs: '0',
                        lg:
                            hideSideBar || sidebarMode === SidebarMode.Small
                                ? '0'
                                : `${drawerWidth}px`,
                    },
                    borderBottom: '1px solid var(--border-color)',
                    boxShadow:
                        '0 1px 3px rgba(16, 24, 40, 0.05), 0 4px 12px rgba(16, 24, 40, 0.03)',
                    transition: 'margin-left 0.2s linear',
                }}
                ref={appBarRef}
                elevation={0}
                className="header"
                color="secondary"
            >
                <Box
                    sx={{
                        width: '100%',
                        display: 'flex',
                        height: `${headerHeight}px`,
                        alignItems: 'center',
                        pr: '30px',
                        justifyContent: 'space-between',
                    }}
                >
                    <Box display={'flex'} alignItems={'center'}>
                        {hideSideBar !== 2 && (
                            <IconButton
                                edge="start"
                                onClick={() => {
                                    if (hideSideBar || isMd) {
                                        setShowMobile(true)
                                    } else {
                                        setSidebarMode(SidebarMode.Normal)
                                    }
                                }}
                                sx={{
                                    ml: '10px',
                                    display: {
                                        xs: 'block',
                                        lg:
                                            hideSideBar ||
                                            sidebarMode === SidebarMode.Small
                                                ? 'block'
                                                : 'none',
                                    },
                                }}
                                color="inherit"
                            >
                                <MenuIcon />
                            </IconButton>
                        )}
                        <Box
                            display={{
                                xs: hideSideBar !== 2 ? 'none' : 'flex',
                                md: 'flex',
                            }}
                            justifyContent={'center'}
                            sx={{
                                width: { xs: '180px', md: `240px` },
                                p: { xs: '5px', md: '15px' },
                            }}
                        >
                            <Box
                                component={'img'}
                                src="/frsistema-logo.png"
                                sx={{ maxWidth: '80%' }}
                            />
                        </Box>
                        {!!accountInfo && (
                            <Stack sx={{ ml: { xs: '15px' } }}>
                                {!isSm && (
                                    <Typography
                                        color="text.main"
                                        fontWeight="500"
                                    >
                                        {configs.companyName} -{' '}
                                        <Box
                                            component={'span'}
                                            sx={{
                                                color: canChangeCashier
                                                    ? 'primary.main'
                                                    : 'text.main',
                                                textDecoration: canChangeCashier
                                                    ? 'underline'
                                                    : 'none',
                                                cursor: canChangeCashier
                                                    ? 'pointer'
                                                    : 'auto',
                                            }}
                                            onClick={() => {
                                                if (canChangeCashier)
                                                    setOpenCashierHashModal(
                                                        true
                                                    )
                                            }}
                                        >
                                            {loggedCashier?.name}
                                        </Box>
                                    </Typography>
                                )}
                                <Breadcrumbs
                                    separator={
                                        <Typography color={'text.main'}>
                                            /
                                        </Typography>
                                    }
                                    sx={{
                                        display: { xs: 'none', md: 'block' },
                                    }}
                                >
                                    <Link
                                        underline="hover"
                                        sx={{
                                            display: 'flex',
                                            alignItems: 'center',
                                        }}
                                        color="text.main"
                                        href={nav?.link}
                                    >
                                        {/* <Box mr={0.5}>{nav?.icon}</Box> */}
                                        {t(nav?.text ?? '')}
                                    </Link>
                                    <Link
                                        underline="hover"
                                        sx={{
                                            display: 'flex',
                                            alignItems: 'center',
                                        }}
                                        color="text.main"
                                        href={nav?.link}
                                    >
                                        {/* <Box mr={0.5}>{subnav?.icon}</Box> */}
                                        {t(subnav?.text ?? '')}
                                    </Link>
                                </Breadcrumbs>
                            </Stack>
                        )}
                    </Box>
                    {/* <Box
                    component="img"
                    src="/logo.jpg"
                    sx={{ padding: '20px', height: '90px' }}
                /> */}
                    <Box
                        sx={{
                            color: 'var(--text-primary)',
                            display: 'flex',
                            alignItems: 'center',
                        }}
                    >
                        {!!accountInfo && (
                            <>
                                {!isSm && (
                                    <Tooltip
                                        title={t('Buscar funcionalidad (F11)')}
                                        arrow
                                    >
                                        <Box
                                            onClick={openCommandPalette}
                                            sx={{
                                                mr: '12px',
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: '6px',
                                                cursor: 'pointer',
                                                border: '1px solid var(--border-color)',
                                                borderRadius: '8px',
                                                px: '8px',
                                                py: '4px',
                                                color: 'text.secondary',
                                                backgroundColor:
                                                    'var(--surface-soft)',
                                                '&:hover': {
                                                    backgroundColor: '#EFF1F6',
                                                    borderColor: '#C2C8D6',
                                                },
                                            }}
                                        >
                                            <Search size={16} />
                                            <Typography
                                                fontSize="12px"
                                                fontWeight={600}
                                            >
                                                F11
                                            </Typography>
                                        </Box>
                                    </Tooltip>
                                )}
                                <Tooltip
                                    title={
                                        mode === 'dark'
                                            ? t('Modo claro')
                                            : t('Modo oscuro')
                                    }
                                    arrow
                                >
                                    <IconButton
                                        onClick={toggleMode}
                                        sx={{
                                            color: 'text.secondary',
                                            mr: '6px',
                                        }}
                                    >
                                        {mode === 'dark' ? (
                                            <LightModeIcon />
                                        ) : (
                                            <DarkModeIcon />
                                        )}
                                    </IconButton>
                                </Tooltip>
                                {!isSm && (
                                    <Button
                                        color="success"
                                        variant="contained"
                                        startIcon={<Tag />}
                                        sx={{
                                            textTransform: 'none',
                                            mr: '10px',
                                        }}
                                        onClick={() => {
                                            navigate('/system/sale/new')
                                        }}
                                    >
                                        {t('Vender ahora')}
                                    </Button>
                                )}
                                {accountInfo.isMaster && (
                                    <Tooltip title="Configs">
                                        <IconButton
                                            onClick={(e) => {
                                                navigate('/system/configs')
                                            }}
                                            sx={{
                                                color: 'text.secondary',
                                                display: {
                                                    xs: 'none',
                                                    md: 'inline-flex',
                                                },
                                            }}
                                        >
                                            <Settings />
                                        </IconButton>
                                    </Tooltip>
                                )}
                                {accountInfo.isMaster && (
                                    <Tooltip title="Logs">
                                        <IconButton
                                            onClick={(e) => {
                                                navigate('/system/log')
                                            }}
                                            sx={{
                                                color: 'text.secondary',
                                                display: {
                                                    xs: 'none',
                                                    md: 'inline-flex',
                                                },
                                            }}
                                        >
                                            <History />
                                        </IconButton>
                                    </Tooltip>
                                )}
                                {canSeeDashboard && (
                                    <Tooltip title="Painel Admin">
                                        <IconButton
                                            onClick={(e) => {
                                                navigate('/system/dashboard')
                                            }}
                                            sx={{
                                                color: 'text.secondary',
                                                display: {
                                                    xs: 'none',
                                                    md: 'inline-flex',
                                                },
                                            }}
                                        >
                                            <LayoutDashboard />
                                        </IconButton>
                                    </Tooltip>
                                )}
                                <Tooltip
                                    title="Documentos"
                                    disableFocusListener
                                >
                                    <IconButton
                                        onClick={(e) => {
                                            loading.hideDocument()
                                            setDocuments([])
                                            setAnchor(e.currentTarget)
                                            setAnchorType('document')
                                        }}
                                        ref={documentRef}
                                        sx={{
                                            color: 'text.secondary',
                                            '&::after':
                                                loading.isLoadingDocument
                                                    ? {
                                                          content: '""',
                                                          position: 'absolute',
                                                          inset: -6,
                                                          borderRadius: '50%',
                                                          backgroundColor:
                                                              'rgba(25, 118, 210, 0.45)',
                                                          animation:
                                                              'breathing 1s cubic-bezier(0.4, 0, 0.2, 1) infinite',
                                                          zIndex: -1,
                                                          filter: 'blur(2px)',
                                                      }
                                                    : {},

                                            '@keyframes breathing': {
                                                '0%, 100%': {
                                                    transform: 'scale(1)',
                                                    opacity: 0.7,
                                                },
                                                '50%': {
                                                    transform: 'scale(1.1)',
                                                    opacity: 1,
                                                },
                                            },
                                        }}
                                    >
                                        <FileText />
                                    </IconButton>
                                </Tooltip>
                                <Tooltip title="Notificações">
                                    <IconButton
                                        onClick={(e) => {
                                            setAnchor(e.currentTarget)
                                            setAnchorType('notification')
                                            setNotificationsNotSeen(0)
                                        }}
                                        sx={{
                                            color: 'text.secondary',
                                            mr: '10px',
                                        }}
                                    >
                                        <Bell />
                                        <Box
                                            sx={{
                                                position: 'absolute',
                                                width: '15px',
                                                height: '15px',
                                                borderRadius: '50%',
                                                backgroundColor: 'error.main',
                                                fontSize: '10px',
                                                right: '2px',
                                                bottom: '4px',
                                                color: 'white',
                                                animation: notificationsNotSeen
                                                    ? 'notification 0.5s infinite'
                                                    : 'none',
                                            }}
                                        >
                                            {notificationsNotSeen}
                                        </Box>
                                    </IconButton>
                                </Tooltip>
                            </>
                        )}
                        {!!accountInfo ? (
                            <IconButton
                                onClick={(e) => {
                                    setAnchor(e.currentTarget)
                                    setAnchorType('user')
                                }}
                            >
                                <Avatar
                                    sx={{
                                        bgcolor: deepOrange[500],
                                        cursor: 'pointer',
                                        width: 38,
                                        height: 38,
                                        fontSize: '15px',
                                        boxShadow:
                                            '0 2px 8px rgba(234, 88, 12, 0.35)',
                                        border: '2px solid #FFFFFF',
                                    }}
                                >
                                    {accountInfo?.name[0]}
                                </Avatar>
                            </IconButton>
                        ) : (
                            <Button
                                color="success"
                                variant="contained"
                                /* startIcon={<SellIcon />} */
                                sx={{
                                    textTransform: 'none',
                                    fontSize: { xs: '12px', md: '14px' },
                                }}
                                onClick={() => {
                                    setOpenLoginModal(true)
                                }}
                            >
                                {t('Acessar el sistema')}
                            </Button>
                        )}
                    </Box>
                </Box>
            </AppBar>
        </>
    )
}

export default Header
